/* eslint-disable max-lines, react/no-multi-comp */
import { useNavigate, useParams } from "react-router-dom";
import { IconChevronRight, IconChevronDown } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { EntityTypeLabels, GET_EXPERIMENTATION } from "./constants";
import {
  getQueryGroupData,
  getQueryJobData,
  getWarehouseCost,
  getExperimentById,
  updateExperimentStatus,
  getTableId,
} from "../../helpers/apis";
import { RelativeComponentLoader } from "../../components/loader";
import styles from "./styles.module.scss";
import { Card, CardContent, Stack } from "@altimateai/lego";
import { ColorInsightTags, ModelTag, Tags } from "../../components/tags";
import { MarkdownEditor, MarkdownEditorHandle } from "../../components/markdownEditor";
import { useMemo, useRef, useState } from "react";
import { uniqueId } from "lodash";
import { Dropdown } from "../../components/form";
import { FlexListSummary } from "../../components/listSummary";
import dayjs from "dayjs";
import { useUsersWithServiceUser } from "../../helpers/hooks";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useBreadcrumb } from "@/components/breadcrumbs";
import type { WarehouseCostResponse } from "../warehouse/types";
import type { QueryGraphDataResponse } from "../query/types";
import { Period } from "../query/types";

interface DataTypeOption {
  label: string;
  value: string;
  prefix: string;
  suffix: string;
}

interface TickPosition {
  x?: number;
  y?: number;
  payload?: { value: string | number };
}

interface CustomYAxisTickProps extends TickPosition {
  dataType?: DataTypeOption;
  [key: string]: unknown;
}

interface CustomXAxisTickProps extends TickPosition {
  [key: string]: unknown;
}

interface GraphProps {
  data: Record<string, unknown>[];
  dataType?: DataTypeOption;
  xDataKey: string;
  yDataKey: string;
}

interface EntityGraphProps {
  entity_type: string;
  entity_rk: string;
  entity_name?: string;
}

const CustomYAxisTick = ({ x = 0, y = 0, payload, dataType }: CustomYAxisTickProps) => (
  <g transform={`translate(${x},${y})`}>
    <text textAnchor="end" dy={5} fill="#66768D">
      {dataType?.prefix ?? ""}
      {payload?.value}
      {dataType?.suffix ?? ""}
    </text>
  </g>
);

const CustomXAxisTick = ({ x = 0, y = 0, payload }: CustomXAxisTickProps) => {
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dx={-30} dy={4} transform="rotate(270)" textAnchor="middle" fill="#66768D">
        {payload?.value}
      </text>
    </g>
  );
};

const DataTypes: DataTypeOption[] = [
  { label: "Cost", value: "cost", prefix: "$", suffix: "" },
  {
    label: "Execution Time",
    value: "execution_time",
    prefix: "",
    suffix: "mins",
  },
  // {
  //   label: "Cost per GB Processed",
  //   value: "cost_per_gb_processed",
  //   prefix: "$",
  //   suffix: "",
  // },
  {
    label: "Bytes Scanned",
    value: "bytes_scanned",
    prefix: "",
    suffix: "GB",
  },
  {
    label: "Spillage (Local)",
    value: "bytes_spilled_local",
    prefix: "",
    suffix: "GB",
  },
  {
    label: "Spillage (Remote)",
    value: "bytes_spilled_remote",
    prefix: "",
    suffix: "GB",
  },
];

const Graph = ({ data, dataType, xDataKey, yDataKey }: GraphProps) => {
  return (
    <ResponsiveContainer minHeight={400} className="mb-4">
      <BarChart data={data} margin={{ top: 40, right: 50, bottom: 50 }}>
        <CartesianGrid />
        <XAxis
          dataKey={xDataKey}
          interval={0}
          label={{ value: "Time", position: "right", fill: "black" }}
          tick={<CustomXAxisTick />}
        />
        <YAxis
          tick={<CustomYAxisTick dataType={dataType} />}
          label={{
            value: dataType?.label,
            fill: "black",
            position: "top",
            offset: 20,
          }}
        />
        <Tooltip />
        <Bar barSize={8} dataKey={yDataKey} fill="#5B9FFF" radius={[5, 5, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
};

const GET_ENTITY_DATA = "GET-ENTITY-DATA";

function isWarehouseCostResponse(value: unknown): value is WarehouseCostResponse {
  if (typeof value !== "object" || value === null) return false;
  if (!("warehouse_cost" in value)) return false;
  const obj = value as { warehouse_cost: unknown };
  return Array.isArray(obj.warehouse_cost);
}

function isQueryGraphDataResponse(value: unknown): value is QueryGraphDataResponse {
  if (typeof value !== "object" || value === null) return false;
  if (!("data" in value)) return false;
  const obj = value as { data: unknown };
  return Array.isArray(obj.data);
}

const EntityGraph = ({ entity_type, entity_rk, entity_name }: EntityGraphProps) => {
  const [dataType, setDataType] = useState("cost");
  const { data: _data, isLoading } = useQuery<
    WarehouseCostResponse | QueryGraphDataResponse | null | undefined
  >({
    queryKey: [GET_ENTITY_DATA, entity_type, entity_rk],
    queryFn: async () => {
      if (entity_type === "datasets" || entity_type === "schema") {
        return null;
      }
      if (entity_type === "warehouse") {
        // entity_rk format: "instance_id/warehouse_rk" or just warehouse_rk
        // Split if it contains a separator, otherwise use as both params
        const parts = entity_rk.split("/");
        const instance_id = parts.length > 1 ? parts[0] : entity_rk;
        const warehouse_rk = parts.length > 1 ? parts[1] : entity_rk;
        return getWarehouseCost(instance_id, warehouse_rk, { period: Period.MONTH }) ?? null;
      }
      if (entity_type === "query_jobs") {
        return getQueryJobData(entity_rk, { period: Period.MONTH }) ?? null;
      }
      if (entity_type === "query_groups") {
        return getQueryGroupData(entity_rk, { period: Period.MONTH }) ?? null;
      }
      return null;
    },
  });
  const [xDataKey, yDataKey] = useMemo(() => {
    if (entity_type === "datasets" || entity_type === "schema") return ["", ""];
    if (entity_type === "warehouse") return ["date", "cost"];
    if (entity_type === "query_jobs") return ["date", dataType];
    if (entity_type === "query_groups") return ["date", dataType];
    return ["", ""];
  }, [dataType, entity_type]);
  if (isLoading) return <RelativeComponentLoader />;
  if (!_data) return null;
  const rawArray: Array<Record<string, unknown>> = isQueryGraphDataResponse(_data)
    ? _data.data
    : isWarehouseCostResponse(_data)
      ? _data.warehouse_cost
      : [];
  const graphDataSource: Array<Record<string, unknown>> = rawArray.map(item =>
    Object.fromEntries(Object.entries(item))
  );
  if (graphDataSource.length === 0) return null;

  const currentDataType = DataTypes.find(x => x.value === dataType);
  return (
    <Card className="mb-0">
      <CardContent className="al-pt-4">
        {entity_type !== "query_jobs" && entity_type !== "query_groups" ? (
          <div className="fs-5 mb-3">{entity_name} cost over time</div>
        ) : (
          <Stack align="center" justify="between" className="mb-3">
            <div className="fs-5">{currentDataType?.label} over time</div>
            <Dropdown
              id="data-type-dropdown"
              options={DataTypes}
              onChange={setDataType}
              value={dataType}
            >
              <div className={styles.chart_data_type}>
                {currentDataType?.label}
                <IconChevronDown size={16} />
              </div>
            </Dropdown>
          </Stack>
        )}
        <div className={styles.warehouse_graph}>
          <Graph
            data={graphDataSource}
            dataType={currentDataType}
            xDataKey={xDataKey}
            yDataKey={yDataKey}
          />
        </div>
      </CardContent>
    </Card>
  );
};

const ExperimentationItem = () => {
  const { experimentId } = useParams();
  useBreadcrumb(experimentId ?? "Details", {
    defaultParents: [{ label: "Experimentation", pathname: "/experimentation" }],
  });
  const [tableId, setTableId] = useState<string>();
  const navigate = useNavigate();
  const hypothesisRef = useRef<MarkdownEditorHandle>(null);
  const instructionRef = useRef<MarkdownEditorHandle>(null);
  const id = useMemo(() => uniqueId("experiment-status-"), []);
  const { users } = useUsersWithServiceUser();
  const queryClient = useQueryClient();
  const { data: experiment, isLoading } = useQuery({
    queryKey: [GET_EXPERIMENTATION, experimentId],
    queryFn: async () => {
      const _experiment = await getExperimentById(experimentId ?? "");
      if (!_experiment) return null;
      const _entity = (_experiment.entities || [])[0];
      if (!_entity) return _experiment;
      if (_entity.entity_type !== "datasets") return _experiment;
      const _tableId = await getTableId({ table_rk: _entity.entity_rk });
      if (_tableId?.id) setTableId(_tableId.id);
      return _experiment;
    },
  });
  const { mutate } = useMutation({
    mutationFn: (data: { experiment_status: string }) =>
      updateExperimentStatus(experimentId ?? "", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [GET_EXPERIMENTATION, experimentId] });
    },
  });

  const insights = useMemo(() => {
    return Array.from(
      new Set(
        (experiment?.entities || [])
          .map(e => e.insights || [])
          .flat()
          .map(i => i.tag_rk)
      )
    );
  }, [experiment?.entities]);

  if (isLoading) return <RelativeComponentLoader />;
  if (!experiment) return null;
  return (
    <Stack direction="column" className="al-h-full">
      <Stack align="center" gap="sm" className="mb-4">
        <h3 className="text-black mb-0 cursor-default">Experimentation</h3>
        <div className={styles.header_icon}>
          <IconChevronRight size={16} />
        </div>
        <h3 className="text-black mb-0">{experiment.name}</h3>
        <div className="spacer" />
        <Dropdown
          id={id}
          value={experiment.experiment_status}
          options={[
            { label: "Planned", value: "Planned" },
            { label: "In Progress", value: "In Progress" },
            { label: "Success", value: "success" },
            { label: "Failure", value: "failure" },
            { label: "Aborted", value: "Aborted" },
          ]}
          onChange={v => {
            mutate({ experiment_status: v });
          }}
        >
          <div className={styles.experiment_status_dropdown}>
            {experiment.experiment_status}
            <div className={styles.arrow_down}>
              <IconChevronDown size={16} />
            </div>
          </div>
        </Dropdown>
      </Stack>
      <FlexListSummary
        summary={[
          {
            label: "Period",
            value: `${dayjs(experiment.experiment_start).format(
              "DD MMM YYYY"
            )} - ${dayjs(experiment.experiment_end).format("DD MMM YYYY")}`,
          },
          {
            label: "Status",
            value: experiment.experiment_status,
          },
          {
            label: "Owner",
            value: users?.find(u => u.id === experiment.created_by)?.first_name ?? "",
          },
          {
            label: "Effort level",
            value: experiment.effort_level ?? "",
          },
        ]}
      />
      <div className={styles.grid_col_2}>
        {(experiment.entities || []).map(e => (
          <EntityGraph {...e} key={e.entity_rk} />
        ))}
        {insights.length > 0 && (
          <Card className="mb-0">
            <CardContent className="al-pt-4">
              <div className="fs-5 mb-3">Insights</div>
              <Tags>
                {insights.map(item => (
                  <ModelTag key={item} label={item} />
                ))}
              </Tags>
            </CardContent>
          </Card>
        )}
        <Card className="mb-0">
          <CardContent className="al-pt-4">
            <div className="fs-5 mb-3">
              {EntityTypeLabels[(experiment.entities || [])?.[0]?.entity_type]}
            </div>
            <ColorInsightTags
              tags={(experiment.entities || []).map(e => e.entity_rk)}
              labelFn={entityRk => {
                const entity = (experiment.entities || []).find(e => e.entity_rk === entityRk);
                return entity?.entity_name ?? entityRk;
              }}
              onClick={entityRk => {
                const item = (experiment.entities || []).find(e => e.entity_rk === entityRk);
                if (!item) return;
                if (item.entity_type === "warehouse") {
                  navigate(`/warehouse/${item.entity_rk}`);
                } else if (item.entity_type === "query_groups") {
                  navigate(`/query/groups/${item.entity_rk}`);
                } else if (item.entity_type === "query_jobs") {
                  navigate(`/query/jobs/${item.entity_rk}`);
                } else if (item.entity_type === "datasets" && tableId) {
                  navigate(`/datasets/${tableId}`);
                }
              }}
            />
          </CardContent>
        </Card>
        <Card className="mb-0">
          <CardContent className="al-pt-4">
            <div className="fs-5 mb-3">Instructions</div>
            <div className={styles.markdown_input}>
              <MarkdownEditor
                ref={instructionRef}
                defaultValue={experiment.instructions}
                readOnly
              />
            </div>
          </CardContent>
        </Card>
        <Card className="mb-0">
          <CardContent className="al-pt-4">
            <div className="fs-5 mb-3">Hypothesis / Reason</div>
            <div className={styles.markdown_input}>
              <MarkdownEditor ref={hypothesisRef} defaultValue={experiment.hypothesis} readOnly />
            </div>
          </CardContent>
        </Card>
      </div>
    </Stack>
  );
};

export { ExperimentationItem };
